<template>
    <div class="apprentice-simple-card">
        <h3 class="card-title">Project: {{ groupIn.name }}</h3>

        <div id="invite-checker-table">
            <design-2021-apprentice-data-table
                :items="groupPairData"
                :fields="fields"
                :show-empty="true">
                <template v-slot:cell(students)="data">
                    <div v-for="student in data.item.group_pair_students">
                        {{ student.user.full_name }}
                    </div>
                </template>

                <template v-slot:cell(teachers)="data">
                    <div v-for="teacher in data.item.group_pair_group_teachers">
                        {{ teacher.user.full_name }}
                    </div>
                </template>

                <template v-slot:cell(status)="data">
                    <b-button
                        class="btn-recycle btn-reopen"
                        v-if="data.item.getStatus(check_option) === 1"
                        disabled
                        variant="warning-icon-only">
                    </b-button>
                    <b-button
                        class="btn-checked btn-approved"
                        v-else-if="data.item.getStatus(check_option) === 2"
                        disabled
                        variant="success-icon-only">
                    </b-button>
                    <b-button
                        class="btn-hourglass-start btn-pending"
                        v-else
                        disabled
                        variant="warning-icon-only">
                    </b-button>
                </template>

                <template v-slot:cell(actions)="data">
                    <b-button
                        class="btn-show btn-view-invite"
                        variant="primary-icon-only"
                        @click="showGroupModal(data.item)">
                    </b-button>
                </template>

                <template #empty="scope">
                    <div class="not-found">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div
                            class="alert-notice group-admin-message"
                            v-if="isGroupAdmin">
                            {{ $t('group_pair.no_groups_found_to_be_managed') }}
                        </div>
                        <div
                            class="alert-notice default-message"
                            v-else>
                            {{ $t('group_pair.you_have_not_been_chosen_for_a_group_yet') }}
                        </div>
                    </div>
                </template>
            </design-2021-apprentice-data-table>
        </div>
        <design-2021-group-pair-index-show-group-modal
            ref="show-group-modal"
            :group-pair-options-in="groupIn.group_pair_options"
            :coordinator-in="!!isGroupAdmin"
            @saveCall="saveCall">
        </design-2021-group-pair-index-show-group-modal>
    </div>
</template>

<script>
import { ords } from '../../../app';
import { groups } from '../../../models/group/Groups';

export default {
    name: 'design-2021-group-pair-check-component',
    props: {
        groupPairGroupsIn: {
            type: Array,
            required: true,
        },
        groupIn: {
            type: Object,
            required: true,
        },
        userIn: {
            type: Object,
            required: true,
        },
        isGroupAdmin: {
            type: Number,
            required: true,
        },
        isGroupCheckMode: {
            type: Number,
            required: false,
            default: 0,
        },
    },
    data() {
        return {
            groups: [],
            check_option: this.groupIn.group_pair_options.check_option,
            fields: [
                {
                    key: 'students',
                    label: this.$t('group_pair.students'),
                    sortable: true,
                },
                {
                    key: 'teachers',
                    label: this.$t('group_pair.teachers'),
                    sortable: true,
                },
                {
                    key: 'subject',
                    label: this.$t('group_pair.subject'),
                    sortable: true,
                },
                {
                    key: 'status',
                    label: 'Status',
                },
                {
                    key: 'actions',
                    label: this.$t('common.view'),
                },
            ],
        };
    },
    methods: {
        showGroupModal(groupPairGroup) {
            this.$refs['show-group-modal'].show(groupPairGroup);
        },
        saveCall(form) {
            let data = {
                remove_teacher_ids: form.remove_teacher_ids,
                status_teacher: form.status_teacher,
                status_coordinator: form.status_coordinator,
                feedback: form.feedback,
                removed_group_pair_student_ids: form.removed_group_pair_student_ids,
            };

            this.alert.saving();

            this.axios.post(this.routes.groupPairUpdateStatus(form.id), data).then(
                (response) => {
                    ords.hold();
                    ords.use('group_pair_groups').update(response.data, form.id);

                    if (form.removed_group_pair_student_ids.length) {
                        ords.use('group_pair_groups').detach(
                            form.id,
                            'group_pair_students',
                            form.removed_group_pair_student_ids,
                        );
                        ords.use('group_pair_students').update(
                            { group_pair_id: null },
                            form.removed_group_pair_student_ids,
                        );
                    }

                    if (form.remove_teacher_ids.length) {
                        ords.use('group_pair_group_teachers').remove(form.remove_teacher_ids);
                    }
                    ords.continue();

                    if (this.groupPairData.length === 0) {
                        this.showNotificationMessage();
                    } else {
                        this.alert.success();
                    }
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        async showNotificationMessage() {
            await this.$nextTick(() => {
                this.alert
                    .confirmWarning(
                        this.$t('group_pair.request_processed'),
                        this.$t('group_pair.all_outstanding_applications_have_been_processed'),
                        this.$t('group_pair.back_to_dashboard'),
                    )
                    .then((value) => {
                        if (!value) {
                            return;
                        }

                        window.location.href = this.routes.baseUrl();
                    });
            });
        },
    },
    mounted() {
        ords.use('user').add(this.userIn);
        ords.use('group_pair_groups').add(this.groupPairGroupsIn);
        ords.use('group_pair_groups')
            .with('group_pair_group_courses', (GroupPairGroupCourses) => {
                GroupPairGroupCourses.with('course');
            })
            .with('group_pair_group_teachers', (GroupPairGroupTeachers) => {
                GroupPairGroupTeachers.with('user', (userCallback) => {
                    userCallback.with('group_pair_group_teachers', (cb) => {
                        cb.with('group_pair_groups');
                    });
                    userCallback.with('group_pair_teachers');
                });
            })
            .with('group_pair_students', (GroupPairStudents) => {
                GroupPairStudents.with('user');
            })
            .get()
            .subscribe((groups) => {
                this.groups = groups;
            });
    },

    computed: {
        groupPairData() {
            if (this.groups.length === 0) {
                return [];
            }

            let groupData = [];
            if (this.isGroupCheckMode) {
                groupData = this.groups.filter((group) => {
                    if (group.accepted === 0) {
                        return true;
                    }
                });
            } else {
                groupData = [...this.groups];
            }

            groupData = groupData.filter((group) => {
                if (this.check_option === 1) {
                    if (!group.group_pair_group_teachers.some((teacher) => teacher.user_id === this.$authUser.id)) {
                        return false;
                    }
                    return this.isGroupCheckMode ? !group.check_teacher : true;
                }

                if (this.check_option === 2) {
                    const is_teacher_of_group = group.group_pair_group_teachers.some(
                        (teacher) => teacher.user_id === this.$authUser.id,
                    );
                    const is_teacher_only = is_teacher_of_group && !this.isGroupAdmin;
                    const is_both = is_teacher_of_group && this.isGroupAdmin;
                    const is_admin_only = !is_teacher_of_group && this.isGroupAdmin;
                    const is_nothing = !is_teacher_of_group && !this.isGroupAdmin;

                    if (is_nothing) {
                        return false;
                    }

                    if (this.isGroupCheckMode) {
                        if (is_teacher_only) {
                            return !group.check_teacher;
                        }

                        if (is_admin_only) {
                            return !group.check_coordinator;
                        }

                        if (is_both) {
                            return !group.check_teacher || !group.check_coordinator;
                        }
                    } else {
                        if (is_teacher_only || is_admin_only || is_both) {
                            return true;
                        }
                    }
                }

                if (this.check_option === 3) {
                    return this.isGroupAdmin && !group.check_coordinator;
                }
            });

            groupData.sort(function (a, b) {
                return a.check_teacher - b.check_teacher;
            });

            return groupData;
        },
    },
};
</script>

<style scoped lang="scss">
.btn-reopen,
.btn-pending {
    &:hover {
        color: $warning;
    }
}

.btn-approved {
    &:hover {
        color: $success;
    }
}

.card-title {
    color: $primary;
    font-weight: $font-weight-bold;
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}
</style>
